/**
 * m3 echo Frontend - Tenant Detail Dialog
 *
 * Dialog showing detailed tenant information including autopilot quota.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AutopilotQuotaCard } from './AutopilotQuotaCard';
import { TenantWithStats } from '../../services/admin';
import { formatMoney } from '../../utils/currency';

interface Props {
  open: boolean;
  onClose: () => void;
  tenant: TenantWithStats | null;
  /**
   * Passed on to the quota card and called when a correction is booked. The
   * tenant list this dialog opens from holds its rows in component state, so
   * the quota column behind the dialog cannot be invalidated - it has to be
   * reloaded.
   */
  onQuotaChanged?: () => void;
}

export const TenantDetailDialog: React.FC<Props> = ({
  open,
  onClose,
  tenant,
  onQuotaChanged,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!tenant) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Mandant: {tenant.display_name}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Basic Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basisinformationen
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{tenant.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Anzeigename
                  </Typography>
                  <Typography variant="body1">{tenant.display_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Typography variant="body1">
                    {tenant.is_active ? 'Aktiv' : 'Inaktiv'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Erstellt am
                  </Typography>
                  <Typography variant="body1">
                    {new Date(tenant.created_at).toLocaleDateString('de-DE')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistiken
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Benutzer
                  </Typography>
                  <Typography variant="h4">{tenant.user_count || 0}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Projekte
                  </Typography>
                  <Typography variant="h4">{tenant.project_count || 0}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">
                    Gesamtkosten
                  </Typography>
                  <Typography variant="h4">
                    {formatMoney(tenant.total_cost, tenant.currency)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Autopilot Quota */}
          <AutopilotQuotaCard tenantId={tenant.id} onQuotaChanged={onQuotaChanged} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantDetailDialog;
